import "./dashboardBanner.css";

import React, { useEffect, useMemo, useState } from "react";

import {
  Button,
  Empty,
  Image,
  message,
  Select,
  Spin,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  EditOutlined,
  ExportOutlined,
  LinkOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import moment from "moment";

import { getBanners, updatePlatformBanner } from "../api";
import BannerFormModal from "./BannerFormModal";
import {
  comboKey,
  compareBannersByCombo,
  getAllAllowedCombos,
  getAvailableSetupOptionsForFilter,
  getAvailableTierOptionsForFilter,
  getSetupLabel,
  PLATFORM_LABEL,
  PLATFORMS,
  SETUP_TAG_COLOR,
  TIER_LABEL,
  TIER_TAG_COLOR,
} from "./constants";

const { TabPane } = Tabs;
const { Option } = Select;

const TAB_ALL = "all";

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const extractList = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp.result)) return resp.result;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const DashboardBanner = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_ALL);
  const [modalState, setModalState] = useState({
    open: false,
    mode: "create",
    banner: null,
  });
  const [togglingId, setTogglingId] = useState(null);
  const [filters, setFilters] = useState({
    status: undefined,
    setup: undefined,
    tier: undefined,
  });

  const hasActiveFilters = Boolean(
    filters.status || filters.setup || filters.tier
  );

  // Setup options depend on the platform tab; tier options depend on both.
  const platformForOptions = activeTab === TAB_ALL ? undefined : activeTab;

  const availableSetupOptions = useMemo(
    () => getAvailableSetupOptionsForFilter(platformForOptions),
    [platformForOptions]
  );

  const availableTierOptions = useMemo(
    () => getAvailableTierOptionsForFilter(platformForOptions, filters.setup),
    [platformForOptions, filters.setup]
  );

  // If a previously chosen filter value is no longer valid in the current
  // (platform, setup) context, clear it so the user doesn't see a "ghost"
  // selection that excludes everything.
  useEffect(() => {
    setFilters((prev) => {
      let next = prev;
      if (
        prev.setup &&
        !availableSetupOptions.some((o) => o.value === prev.setup)
      ) {
        next = { ...next, setup: undefined };
      }
      if (
        prev.tier &&
        !availableTierOptions.some((o) => o.value === prev.tier)
      ) {
        next = { ...next, tier: undefined };
      }
      return next;
    });
  }, [availableSetupOptions, availableTierOptions]);

  const handleFilterChange = (key) => (value) =>
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      // Changing setup invalidates a previously chosen tier when that tier
      // isn't allowed under the new setup.
      if (key === "setup") {
        const allowedTiers = getAvailableTierOptionsForFilter(
          platformForOptions,
          value
        );
        if (prev.tier && !allowedTiers.some((o) => o.value === prev.tier)) {
          next.tier = undefined;
        }
      }
      return next;
    });

  const handleClearFilters = () =>
    setFilters({ status: undefined, setup: undefined, tier: undefined });

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const resp = await getBanners();
      setBanners(extractList(resp));
    } catch (err) {
      message.error(err?.message || "Failed to load banners.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const counts = useMemo(() => {
    const tagmango = banners.filter(
      (b) => b.platform === PLATFORMS.TAGMANGO
    ).length;
    const whitelabel = banners.filter(
      (b) => b.platform === PLATFORMS.WHITELABEL
    ).length;
    return { all: banners.length, tagmango, whitelabel };
  }, [banners]);

  // Disable "Add a Banner" once every Domain × Setup × Tier combo is taken.
  // We dedupe by combo key so legacy duplicates don't make the count look
  // higher than the universe of allowed combos.
  const allCombosExhausted = useMemo(() => {
    const allCombos = getAllAllowedCombos();
    const usedKeys = new Set(
      banners
        .filter((b) => b.platform && b.platformSetup && b.tier)
        .map((b) =>
          comboKey({
            platform: b.platform,
            platformSetup: b.platformSetup,
            tier: b.tier,
          })
        )
    );
    return allCombos.every((c) => usedKeys.has(comboKey(c)));
  }, [banners]);

  const visibleBanners = useMemo(() => {
    const filtered = banners.filter((b) => {
      if (activeTab !== TAB_ALL && b.platform !== activeTab) return false;
      if (filters.status === "active" && !b.isActive) return false;
      if (filters.status === "inactive" && b.isActive) return false;
      if (filters.setup && b.platformSetup !== filters.setup) return false;
      if (filters.tier && b.tier !== filters.tier) return false;
      return true;
    });
    // Sort by canonical combo order; within the same combo, active banner
    // first then by most-recently-created.
    return [...filtered].sort((a, b) => {
      const byCombo = compareBannersByCombo(a, b);
      if (byCombo !== 0) return byCombo;
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [banners, activeTab, filters]);

  const handleOpenCreate = () => {
    setModalState({ open: true, mode: "create", banner: null });
  };

  const handleOpenEdit = (banner) => {
    setModalState({ open: true, mode: "edit", banner });
  };

  const handleCloseModal = () => {
    setModalState((prev) => ({ ...prev, open: false }));
  };

  const handleSaved = () => {
    handleCloseModal();
    fetchBanners();
  };

  const handleToggleActive = async (banner, nextActive) => {
    try {
      setTogglingId(banner._id);
      // Optimistic update
      setBanners((prev) =>
        prev.map((b) =>
          b._id === banner._id ? { ...b, isActive: nextActive } : b
        )
      );
      const resp = await updatePlatformBanner(
        { isActive: nextActive },
        banner._id
      );
      const ok = resp?.status === 200 || resp?.code === 200 || resp?.code === 0;
      if (!ok) {
        throw new Error(resp?.message || "Failed to update status.");
      }
      message.success(nextActive ? "Banner activated." : "Banner deactivated.");
    } catch (err) {
      // Revert on failure
      setBanners((prev) =>
        prev.map((b) =>
          b._id === banner._id ? { ...b, isActive: !nextActive } : b
        )
      );
      if (err?.status === 409) {
        message.error("Another banner already exists for this combination.");
      } else {
        message.error(err?.message || "Failed to update status.");
      }
    } finally {
      setTogglingId(null);
    }
  };

  const renderBannerCard = (banner) => {
    const setupLabel = getSetupLabel(banner.platform, banner.platformSetup);
    const tierLabel = TIER_LABEL[banner.tier] || banner.tier;
    return (
      <div className="bannerCard" key={banner._id}>
        <div className="bannerCardImageWrap">
          {banner.bannerUrl ? (
            <Image
              src={banner.bannerUrl}
              alt="banner"
              className="bannerCardImage"
              wrapperClassName="bannerCardImageInner"
              preview={{ mask: "Click to view" }}
            />
          ) : (
            <div className="bannerCardImagePlaceholder" />
          )}
        </div>
        <div className="bannerCardBody">
          <div className="bannerCardHeader">
            <Typography.Text strong className="bannerCardPlatform">
              {PLATFORM_LABEL[banner.platform] || banner.platform}
            </Typography.Text>
            <Tag
              color={SETUP_TAG_COLOR[banner.platformSetup]}
              className="bannerCardTag"
            >
              {setupLabel}
            </Tag>
            <Tag color={TIER_TAG_COLOR[banner.tier]} className="bannerCardTag">
              {tierLabel}
            </Tag>
          </div>
          <div className="bannerCardMeta">
            {banner.redirectUrl ? (
              <a
                href={banner.redirectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bannerCardMetaItem bannerCardMetaLink"
                title="Open in new tab"
              >
                <LinkOutlined />
                <span className="bannerCardMetaLinkText">
                  {banner.redirectUrl}
                </span>
                <ExportOutlined className="bannerCardMetaLinkIndicator" />
              </a>
            ) : (
              <span className="bannerCardMetaItem bannerCardMetaMuted">
                <LinkOutlined /> No redirect URL
              </span>
            )}
            <span className="bannerCardMetaItem">
              <CalendarOutlined />{" "}
              {moment(banner.createdAt).format("MMM DD, YYYY")}
            </span>
          </div>
        </div>
        <div className="bannerCardActions">
          <Button
            icon={<EditOutlined />}
            className="bannerEditBtn"
            onClick={() => handleOpenEdit(banner)}
          >
            Edit
          </Button>
          <Switch
            checked={Boolean(banner.isActive)}
            loading={togglingId === banner._id}
            onChange={(checked) => handleToggleActive(banner, checked)}
          />
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="bannerEmptyState">
      <Empty description="No banners yet." />
    </div>
  );

  const renderListContent = () => {
    if (loading) {
      return (
        <div className="bannerLoadingWrapper">
          <Spin />
        </div>
      );
    }
    if (visibleBanners.length === 0) {
      return renderEmptyState();
    }
    return (
      <div className="bannerCardList">
        {visibleBanners.map(renderBannerCard)}
      </div>
    );
  };

  return (
    <div className="dashboardBannerPage">
      <div className="dashboardBannerHeader">
        <Typography.Title level={4} className="dashboardBannerTitle">
          Dashboard Banner
        </Typography.Title>
        <Tooltip
          placement="topRight"
          arrowPointAtCenter
          title={
            allCombosExhausted
              ? "All allowed combinations already have a banner."
              : ""
          }
        >
          {/* Tooltip needs a wrapping span to listen on a disabled button. */}
          <span className="addBannerBtnWrap">
            <Button
              icon={<PlusOutlined />}
              className="siteBtn accentBtnV1"
              onClick={handleOpenCreate}
              disabled={allCombosExhausted}
            >
              Add a Banner
            </Button>
          </span>
        </Tooltip>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className="dashboardBannerTabs"
      >
        <TabPane
          tab={
            <span>
              All <span className="tabCountPill">{counts.all}</span>
            </span>
          }
          key={TAB_ALL}
        />
        <TabPane
          tab={
            <span>
              TagMango <span className="tabCountPill">{counts.tagmango}</span>
            </span>
          }
          key={PLATFORMS.TAGMANGO}
        />
        <TabPane
          tab={
            <span>
              White Label{" "}
              <span className="tabCountPill">{counts.whitelabel}</span>
            </span>
          }
          key={PLATFORMS.WHITELABEL}
        />
      </Tabs>

      <div className="bannerFilterBar">
        <Select
          allowClear
          value={filters.status}
          onChange={handleFilterChange("status")}
          placeholder="Status"
          className="bannerFilterSelect"
          dropdownMatchSelectWidth={false}
        >
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
        <Select
          allowClear
          value={filters.setup}
          onChange={handleFilterChange("setup")}
          placeholder="Setup"
          className="bannerFilterSelect"
          dropdownMatchSelectWidth={false}
        >
          {availableSetupOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
        <Select
          allowClear
          value={filters.tier}
          onChange={handleFilterChange("tier")}
          placeholder="Tier"
          className="bannerFilterSelect"
          dropdownMatchSelectWidth={false}
          disabled={availableTierOptions.length === 0}
        >
          {availableTierOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
        {hasActiveFilters && (
          <Button
            type="link"
            className="bannerFilterClearBtn"
            onClick={handleClearFilters}
          >
            Clear filters
          </Button>
        )}
      </div>

      {renderListContent()}

      {modalState.open && (
        <BannerFormModal
          open={modalState.open}
          mode={modalState.mode}
          banner={modalState.banner}
          banners={banners}
          onClose={handleCloseModal}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default DashboardBanner;
